import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import {
  getGoogleAuthStatus,
  getGoogleAuthUrl,
  disconnectGoogleAccount,
  type GoogleAuthStatus,
} from '../services/googleSheetsService';

interface UserProfile {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  authProvider: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  subscriptionEndDate?: string;
  createdAt: string;
}

interface SubscriptionDetails {
  tier: string;
  status: string;
  endDate?: string;
  hasStripeSubscription: boolean;
  stripeSubscriptions: Array<{
    id: string;
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  }>;
}

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  date: string;
  periodStart: string;
  periodEnd: string;
  hostedInvoiceUrl: string;
  invoicePdf: string;
  number: string;
}

export default function Account() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'subscription' | 'billing' | 'integrations'>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Google OAuth state
  const [googleAuthStatus, setGoogleAuthStatus] = useState<GoogleAuthStatus | null>(null);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Cancellation form
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
      setCompanyName(profile.companyName || '');
    }
  }, [profile]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [profileRes, subscriptionRes, invoicesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/user/profile`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/subscription/status`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/subscription/invoices`, { credentials: 'include' }),
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data.user);
      }

      if (subscriptionRes.ok) {
        const data = await subscriptionRes.json();
        setSubscription(data.subscription);
      }

      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data.invoices || []);
      }

      // Fetch Google auth status
      await fetchGoogleAuthStatus();
    } catch (error) {
      console.error('Failed to fetch account data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGoogleAuthStatus = async () => {
    try {
      const status = await getGoogleAuthStatus();
      setGoogleAuthStatus(status);
    } catch (error) {
      console.error('Failed to fetch Google auth status:', error);
    }
  };

  const handleConnectGoogle = async () => {
    setIsConnectingGoogle(true);
    try {
      const authUrl = await getGoogleAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to start Google OAuth:', error);
      setMessage({ type: 'error', text: 'Failed to connect to Google' });
      setIsConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      await disconnectGoogleAccount();
      setMessage({ type: 'success', text: 'Google account disconnected successfully' });
      await fetchGoogleAuthStatus();
    } catch (error) {
      console.error('Failed to disconnect Google:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect Google account' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firstName, lastName, companyName }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        await refreshUser();
        await fetchData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setIsSaving(false);
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      setIsSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/user/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSubscription = async (immediately: boolean = false) => {
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          immediately,
          reason: cancelReason,
          feedback: cancelFeedback,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        setShowCancelModal(false);
        setCancelReason('');
        setCancelFeedback('');
        await fetchData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to cancel subscription' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cancel subscription' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/portal`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'pro':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500';
      case 'starter':
        return 'bg-gradient-to-r from-blue-400 to-cyan-400';
      case 'enterprise':
        return 'bg-slate-700';
      default:
        return 'bg-slate-700';
    }
  };

  const getTierName = (tier: string) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold text-slate-100">Account Settings</h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage your profile, security, and subscription
          </p>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="px-4 sm:px-0 mb-6">
            <div
              className={`rounded-lg p-4 ${
                message.type === 'success'
                  ? 'bg-green-500/10 border border-green-500 text-green-300'
                  : 'bg-red-500/10 border border-red-500 text-red-300'
              }`}
            >
              {message.text}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-4 sm:px-0 mb-6">
          <div className="border-b border-slate-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'profile', label: 'Profile', icon: '👤' },
                { id: 'password', label: 'Password', icon: '🔒' },
                { id: 'subscription', label: 'Subscription', icon: '💳' },
                { id: 'billing', label: 'Billing History', icon: '📄' },
                { id: 'integrations', label: 'Integrations', icon: '🔗' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 sm:px-0">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700 p-6">
              <h2 className="text-xl font-bold text-slate-100 mb-4">Profile Information</h2>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-slate-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-slate-300 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-slate-300 mb-2">
                    Company Name (Optional)
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-slate-400">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Account Type</label>
                  <input
                    type="text"
                    value={profile?.authProvider === 'google' ? 'Google Account' : 'Email Account'}
                    disabled
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400 cursor-not-allowed"
                  />
                </div>
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700 p-6">
              <h2 className="text-xl font-bold text-slate-100 mb-4">Change Password</h2>
              {profile?.authProvider !== 'local' && profile?.authProvider !== 'email' ? (
                <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded">
                  <p className="text-sm text-yellow-300">
                    You're signed in with {profile?.authProvider}. Password changes are not available for OAuth accounts.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-slate-300 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-slate-400">Must be at least 8 characters long</p>
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-100">Current Plan</h2>
                    <p className="text-sm text-slate-400 mt-1">Manage your subscription</p>
                  </div>
                  <span className={`${getTierColor(subscription?.tier || 'free')} text-white px-4 py-2 rounded-full text-sm font-semibold`}>
                    {getTierName(subscription?.tier || 'free')}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-900 rounded-lg p-4">
                    <p className="text-sm text-slate-400">Status</p>
                    <p className="text-lg font-semibold text-slate-100 capitalize">{subscription?.status}</p>
                  </div>
                  {subscription?.endDate && (
                    <div className="bg-slate-900 rounded-lg p-4">
                      <p className="text-sm text-slate-400">Renewal Date</p>
                      <p className="text-lg font-semibold text-slate-100">
                        {new Date(subscription.endDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {subscription?.stripeSubscriptions?.[0]?.cancelAtPeriodEnd && (
                  <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded mb-6">
                    <p className="text-sm text-yellow-300">
                      Your subscription is set to cancel on{' '}
                      {new Date(subscription.stripeSubscriptions[0].currentPeriodEnd).toLocaleDateString()}.
                      You'll still have access until then.
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-4">
                  {subscription?.tier !== 'free' && (
                    <>
                      <button
                        onClick={handleManageSubscription}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-colors"
                      >
                        Manage via Stripe
                      </button>
                      {!subscription?.stripeSubscriptions?.[0]?.cancelAtPeriodEnd && (
                        <button
                          onClick={() => setShowCancelModal(true)}
                          className="bg-slate-700 text-slate-300 px-6 py-2 rounded-lg font-semibold hover:bg-slate-600 transition-colors"
                        >
                          Cancel Subscription
                        </button>
                      )}
                    </>
                  )}
                  {subscription?.tier === 'free' && (
                    <a
                      href="/pricing"
                      className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-colors inline-block"
                    >
                      Upgrade Plan
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Billing History Tab */}
          {activeTab === 'billing' && (
            <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700 p-6">
              <h2 className="text-xl font-bold text-slate-100 mb-4">Billing History</h2>
              {invoices.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-4 text-slate-400">No billing history available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-700">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Invoice
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                            {new Date(invoice.date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                            {invoice.number || invoice.id.substring(0, 8)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                            ${invoice.amount.toFixed(2)} {invoice.currency}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.status === 'paid'
                                ? 'bg-green-500/10 text-green-400'
                                : 'bg-yellow-500/10 text-yellow-400'
                            }`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-2">
                              <a
                                href={invoice.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300"
                              >
                                View
                              </a>
                              <span className="text-slate-600">|</span>
                              <a
                                href={invoice.invoicePdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300"
                              >
                                Download PDF
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700 p-6">
              <h2 className="text-xl font-bold text-slate-100 mb-4">Connected Services</h2>
              <p className="text-slate-400 mb-6">
                Connect third-party services to enhance your B2B CSV experience
              </p>

              {/* Google Sheets Integration */}
              <div className="bg-slate-900 rounded-lg border border-slate-700 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="bg-white rounded-lg p-3 flex items-center justify-center">
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                        <path d="M19.5 2h-15A2.5 2.5 0 002 4.5v15A2.5 2.5 0 004.5 22h15a2.5 2.5 0 002.5-2.5v-15A2.5 2.5 0 0019.5 2z" fill="#0F9D58"/>
                        <path d="M14 17v-2h2v2h-2zm0-4V7h2v6h-2zm-4 4v-6h2v6h-2zm0-8V7h2v2h-2zm-4 8v-4h2v4H6zm0-6V7h2v4H6z" fill="#F4F4F4"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-100 mb-2">Google Sheets</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Export your search results and enriched data directly to Google Sheets
                      </p>
                      {googleAuthStatus?.hasGoogleAuth && googleAuthStatus?.isTokenValid ? (
                        <div className="flex items-center space-x-2">
                          <span className="flex items-center text-sm text-green-400">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Connected
                          </span>
                          {googleAuthStatus?.tokenExpiry && (
                            <span className="text-xs text-slate-500">
                              • Expires {new Date(googleAuthStatus.tokenExpiry).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="flex items-center text-sm text-slate-500">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Not connected
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    {googleAuthStatus?.hasGoogleAuth && googleAuthStatus?.isTokenValid ? (
                      <button
                        onClick={handleDisconnectGoogle}
                        disabled={isSaving}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Disconnecting...' : 'Disconnect'}
                      </button>
                    ) : (
                      <button
                        onClick={handleConnectGoogle}
                        disabled={isConnectingGoogle}
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isConnectingGoogle ? 'Connecting...' : 'Connect'}
                      </button>
                    )}
                  </div>
                </div>

                {googleAuthStatus?.hasGoogleAuth && googleAuthStatus?.isTokenValid && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-semibold text-slate-300 mb-2">Features Available:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center text-sm text-slate-400">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Export saved searches to Google Sheets
                      </li>
                      <li className="flex items-center text-sm text-slate-400">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Export LLC enrichment data to Google Sheets
                      </li>
                      <li className="flex items-center text-sm text-slate-400">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Auto-formatted sheets with headers and styling
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-xl max-w-lg w-full p-6 border border-slate-700">
            <h3 className="text-xl font-bold text-slate-100 mb-4">Cancel Subscription</h3>
            <p className="text-slate-300 mb-4">
              We're sorry to see you go! Your subscription will remain active until the end of your billing period.
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Why are you cancelling? (Optional)
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a reason</option>
                  <option value="too_expensive">Too expensive</option>
                  <option value="not_enough_features">Not enough features</option>
                  <option value="switching_service">Switching to another service</option>
                  <option value="no_longer_needed">No longer needed</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Additional feedback (Optional)
                </label>
                <textarea
                  value={cancelFeedback}
                  onChange={(e) => setCancelFeedback(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us how we can improve..."
                ></textarea>
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => handleCancelSubscription(false)}
                disabled={isSaving}
                className="flex-1 bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                  setCancelFeedback('');
                }}
                disabled={isSaving}
                className="flex-1 bg-slate-700 text-slate-300 px-6 py-2 rounded-lg font-semibold hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
